import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import ContentCard from '../components/ContentCard';
import ImageViewer from '../components/ImageViewer';
import '../components/PageNav.css';
import './GuestShare.css';

const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;

// The read-only guest view of a published place: browse level by level, or search.
// It shows only the published copy and has no way to change anything.
function GuestShare() {
  const { token } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [status, setStatus] = useState('loading'); // loading | ready | gone
  const [text, setText] = useState(params.get('q') || '');

  const placeParam = params.get('place');
  const itemParam = params.get('item');
  const q = (params.get('q') || '').trim();

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const response = await fetch(`/api/share/${token}`);
        if (!response.ok) throw new Error('gone');
        const body = await response.json();
        if (cancelled) return;
        setData(body);
        setStatus('ready');
        document.title = body.title;
      } catch (e) {
        if (!cancelled) setStatus('gone');
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [token]);

  useEffect(() => {
    setText(params.get('q') || '');
  }, [params]);

  const model = useMemo(() => {
    if (!data) return null;
    const places = new Map(data.places.map((p) => [p.id, p]));
    const kids = new Map();
    const items = new Map();
    for (const p of data.places) {
      const key = p.parentId || '';
      if (!kids.has(key)) kids.set(key, []);
      kids.get(key).push(p);
    }
    for (const i of data.items) {
      if (!items.has(i.placeId)) items.set(i.placeId, []);
      items.get(i.placeId).push(i);
    }
    const byName = (a, b) => a.name.localeCompare(b.name);
    kids.forEach((list) => list.sort(byName));
    items.forEach((list) => list.sort(byName));
    const chain = (placeId) => {
      const out = [];
      for (let cur = places.get(placeId); cur && out.length < 50; cur = places.get(cur.parentId)) out.unshift(cur);
      return out;
    };
    return { places, kids, items, chain };
  }, [data]);

  if (status === 'loading') return <div className="guest"><p className="guest-note">Loading...</p></div>;
  if (status === 'gone') {
    return (
      <div className="guest">
        <h1>This link is no longer available</h1>
        <p className="guest-note">Ask your host for a new link or QR code.</p>
      </div>
    );
  }

  const { places, kids, items, chain } = model;
  const imageUrl = (key) => `/api/share/${token}/image/${key}?v=${data.version}`;
  const countsOf = (p) => {
    const c = (kids.get(p.id) || []).length;
    const n = (items.get(p.id) || []).length;
    const parts = [];
    if (c) parts.push(plural(c, 'compartment'));
    if (n) parts.push(plural(n, 'item'));
    return parts.length ? parts.join(' · ') : 'Empty';
  };
  const pathOf = (placeId) => chain(placeId).map((p) => p.name).join(' › ');

  const item = itemParam ? data.items.find((i) => i.id === itemParam) : null;
  const place = places.get(placeParam) || places.get(data.rootId);
  const placeLink = (p) => (p.id === data.rootId ? '?' : `?place=${p.id}`);

  const search = (event) => {
    event.preventDefault();
    const t = text.trim();
    navigate(t ? `?q=${encodeURIComponent(t)}` : '?');
  };

  const goBack = () => {
    const index = window.history.state && window.history.state.idx;
    if (index > 0) navigate(-1);
    else if (item) navigate(placeLink(places.get(item.placeId) || place), { replace: true });
    else if (q) navigate('?', { replace: true });
    else if (place.parentId) navigate(placeLink(places.get(place.parentId)), { replace: true });
  };

  // trail of places leading to what is shown (the current place or item's place)
  const trailPlaces = item ? chain(item.placeId) : q ? [] : chain(place.id).slice(0, -1);
  const current = item ? item.name : q ? 'Search' : place.name;
  const showBack = !!item || !!q || !!place.parentId;

  let results = null;
  if (q) {
    const needle = q.toLowerCase();
    const hit = (o) => o.name.toLowerCase().includes(needle) || (o.description || '').toLowerCase().includes(needle);
    results = {
      places: data.places.filter((p) => p.id !== data.rootId && hit(p)).sort((a, b) => pathOf(a.id).localeCompare(pathOf(b.id))),
      items: data.items.filter(hit).sort((a, b) => a.name.localeCompare(b.name))
    };
  }

  return (
    <div className="guest">
      <header className="guest-header">
        <h1>{data.title}</h1>
        <span className="guest-updated">Updated {new Date(data.publishedAt).toLocaleDateString()}</span>
      </header>

      <form className="guest-search" onSubmit={search} role="search">
        <input
          type="search"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={`Search ${data.title}`}
          aria-label={`Search ${data.title}`}
          autoComplete="off"
          enterKeyHint="search"
        />
        <button type="submit" className="guest-btn" disabled={!text.trim() && !q}>Search</button>
      </form>

      {showBack && (
      <div className="pagenav">
        {showBack && (
          <button type="button" className="pagenav-back" onClick={goBack}>
            <svg viewBox="0 0 8 14" aria-hidden="true" className="pagenav-back-icon">
              <path d="M7 1L1 7l6 6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            Back
          </button>
        )}
        <div className="pagenav-scroll guest-scroll" ref={(el) => { if (el) el.scrollLeft = el.scrollWidth; }}>
          <nav className="pagenav-trail" aria-label="Breadcrumb">
            {(item || q || place.id !== data.rootId) && (
              <>
                <Link to="?" className="pagenav-link">{data.title}</Link>
                <svg className="pagenav-chevron" viewBox="0 0 8 14" aria-hidden="true"><path d="M1 1l6 6-6 6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
              </>
            )}
            {trailPlaces.filter((p) => p.id !== data.rootId).map((p) => (
              <React.Fragment key={p.id}>
                <Link to={placeLink(p)} className="pagenav-link">{p.name}</Link>
                <svg className="pagenav-chevron" viewBox="0 0 8 14" aria-hidden="true"><path d="M1 1l6 6-6 6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
              </React.Fragment>
            ))}
            {(item || q || place.id !== data.rootId) && <span className="pagenav-current">{current}</span>}
          </nav>
        </div>
      </div>
      )}

      {results ? (
        <>
          <h2 className="guest-summary">
            {results.places.length + results.items.length === 0
              ? `Nothing found for “${q}”`
              : `${plural(results.places.length + results.items.length, 'match')} for “${q}”`}
          </h2>
          <div className="card-list">
            {results.places.map((p) => (
              <ContentCard
                key={p.id}
                to={placeLink(p)}
                imageUrl={p.image ? imageUrl('p-' + p.id) : null}
                placeholder="🗄️"
                title={p.name}
                subtitle={`In ${pathOf(p.parentId)}`}
                meta={countsOf(p)}
              />
            ))}
            {results.items.map((i) => (
              <ContentCard
                key={i.id}
                to={`?item=${i.id}`}
                imageUrl={i.image ? imageUrl('i-' + i.id) : null}
                placeholder="📦"
                title={i.name}
                subtitle={`In ${pathOf(i.placeId)}`}
                meta={i.count > 1 ? `Count: ${i.count}` : null}
              />
            ))}
          </div>
        </>
      ) : item ? (
        <article className="guest-item">
          {item.image && <ImageViewer src={imageUrl('i-' + item.id)} alt={item.name} className="guest-item-image" />}
          <h2>{item.name}</h2>
          {item.count > 1 && <p className="guest-count">Quantity: {item.count}</p>}
          {item.description && <p className="guest-description">{item.description}</p>}
          <p className="guest-where">
            <strong>Where to find it:</strong> {pathOf(item.placeId)}
          </p>
          <Link to={placeLink(places.get(item.placeId))} className="guest-link">See everything in {places.get(item.placeId).name} ›</Link>
        </article>
      ) : (
        <>
          {place.image && (
            <div className="guest-picture">
              <ImageViewer src={imageUrl('p-' + place.id)} alt={place.name} className="guest-place-image" />
            </div>
          )}
          {place.description && <p className="guest-description">{place.description}</p>}

          {(kids.get(place.id) || []).length > 0 && (
            <section>
              <h3 className="guest-heading">Compartments ({kids.get(place.id).length})</h3>
              <div className="card-list">
                {kids.get(place.id).map((p) => (
                  <ContentCard
                    key={p.id}
                    to={placeLink(p)}
                    imageUrl={p.image ? imageUrl('p-' + p.id) : null}
                    placeholder="🗄️"
                    title={p.name}
                    subtitle={p.description}
                    meta={countsOf(p)}
                  />
                ))}
              </div>
            </section>
          )}

          {(items.get(place.id) || []).length > 0 && (
            <section>
              <h3 className="guest-heading">Items ({items.get(place.id).length})</h3>
              <div className="card-list">
                {items.get(place.id).map((i) => (
                  <ContentCard
                    key={i.id}
                    to={`?item=${i.id}`}
                    imageUrl={i.image ? imageUrl('i-' + i.id) : null}
                    placeholder="📦"
                    title={i.name}
                    subtitle={i.description}
                    meta={i.count > 1 ? `Count: ${i.count}` : null}
                  />
                ))}
              </div>
            </section>
          )}

          {(kids.get(place.id) || []).length === 0 && (items.get(place.id) || []).length === 0 && (
            <p className="guest-note">Nothing has been listed here.</p>
          )}
        </>
      )}
    </div>
  );
}

export default GuestShare;
